import { TableConfig } from './types';
import {
    getTableLayoutFromUserSettings,
    saveTableLayoutToUserSettings,
} from '@/core/components/table/tableHelpers';
import { getTableCaption, updateTableHeaderWrap } from './tableHelpers';
import { copyToClipboard } from '@/core/helpers';
import notify from 'devextreme/ui/notify';
import { localize } from '@/core/filters';

function config(tableThis?: any): Partial<TableConfig> {
    const staticConfig: Partial<TableConfig> = {
        repaintChangesOnly: true,
        wordWrapEnabled: true,
        height: '100%',
        showBorders: true,
        columnResizingMode: 'widget',
        allowColumnReordering: true,
        allowColumnResizing: true,
        columnAutoWidth: true,
        hoverStateEnabled: true,
        columnChooser: {
            enabled: true,
            mode: 'dragAndDrop',
        },
        columnFixing: {
            enabled: true,
        },
        scrolling: {
            mode: 'virtual',
            useNative: false,
            scrollByThumb: true,
            scrollByContent: true,
            showScrollbar: 'always',
        },
        filterRow: {
            visible: true,
        },
        headerFilter: {
            visible: true,
        },
        groupPanel: {
            visible: true,
        },
        grouping: {
            autoExpandAll: true,
        },
        sorting: {
            mode: 'multiple',
        },
        editing: {
            mode: 'cell',
            refreshMode: 'repaint',
            allowUpdating: false,
        }
    };

    let dynamicConfig = {};

    if (tableThis && tableThis.tableKey) {
        dynamicConfig = {
            stateStoring: {
                enabled: true,
                type: 'custom',
                customLoad: () => getTableLayoutFromUserSettings(tableThis.tableKey)
            },
            export: {
                enabled: true,
                fileName: getTableCaption(tableThis.tableKey),
            },
        };
    }

    const functions: Partial<TableConfig> = {

        onContextMenuPreparing(e: any) {
            const textValue: string = e.targetElement[0].innerText.trim();
            if (e.target === 'content' && textValue !== '') {
                e.items = [];
                e.items.push({
                    text: localize('ContextMenu_CopyToClipboard'),
                    onItemClick: () => {
                        copyToClipboard(textValue);
                        notify(`"${textValue}" ` + localize('Notify_CopyToClipboard'), 'success', 1500);
                    }
                });
            }
        },

        onToolbarPreparing(e) {
            const { toolbarOptions } = e;

            if (toolbarOptions && toolbarOptions.items) {
                toolbarOptions.items = toolbarOptions.items.filter(
                    (i: any) => i.name === 'groupPanel'
                );
            }
        },
        // Logic here will replace default dxdrag handler for table header elements,
        // by our custom dragHandler.
        // With this handler table will scroll horizontally during the column reordering.
        // ! Copy-paste from devExpress team code example:
        // tslint:disable-next-line: max-line-length
        // Ticket: https://www.devexpress.com/Support/Center/Question/Details/T338061/datagrid-it-is-not-possible-to-drag-a-column-to-the-last-position-in-the-grid-when
        // Code example: https://codepen.io/anon/pen/MZBGBg?editors=1010
        onContentReady(el: any) {
            let timerId: any;

            if (el.component.getCombinedFilter() !== undefined) {
                el.component.option('haveFilters', true);
            } else {
                el.component.option('haveFilters', false);
            }

            function resetFilters() {
                tableThis.clearFilter();
            }

            function getBoundingRect($headerContent: any) {
                const $transparentCell = $headerContent
                    .parent()
                    .find('.dx-header-row > td.dx-pointer-events-none');
                const boundingElement = $transparentCell.length
                    ? $transparentCell.get(0)
                    : $headerContent.get(0);
                return boundingElement.getBoundingClientRect();
            }
            function scrollTo(grid: any, diff: any) {
                const scrollable = grid.getScrollable();
                let scrollLeft = scrollable.scrollLeft();
                const scrollWidth = scrollable.scrollWidth();
                const $separator = grid
                    .element()
                    .find('.dx-datagrid-columns-separator');
                const step = diff === 0 ? -10 : 10;

                $separator.addClass(
                    'dx-datagrid-columns-separator-transparent'
                );

                timerId = setInterval(() => {
                    const dragHeader = (document.getElementsByClassName('s-controls')[0] as HTMLElement);

                    if (
                        scrollWidth - scrollLeft === diff ||
                        !(dragHeader.offsetWidth > 0 && dragHeader.offsetHeight > 0)
                    ) {
                        clearInterval(timerId);
                    } else {
                        scrollLeft += step;

                        const dragOptions = grid.getView('draggingHeaderView')
                            ._dragOptions;

                        if (dragOptions) {
                            dragOptions.pointsByTarget = null;
                        }

                        grid.getController('draggingHeader').hideSeparators();

                        scrollable.scrollTo({ left: scrollLeft });
                    }
                });
            }
            function dragHandler(e: any) {
                const grid = e.data.grid;
                const $headerContent = grid
                    .element()
                    .find(
                        '.dx-datagrid-headers > .dx-datagrid-content:not(.dx-datagrid-content-fixed)'
                    );
                const headerContentWidth = $headerContent.width();
                const boundingClientRect = getBoundingRect($headerContent);

                clearInterval(timerId);

                if (e.pageX > boundingClientRect.right) {
                    scrollTo(grid, headerContentWidth);
                } else if (e.pageX < boundingClientRect.left) {
                    scrollTo(grid, 0);
                }
            }

            el.element.find('.dx-header-row > td').off('dxdrag', dragHandler);
            el.element.find('.dx-header-row > td').on('dxdrag', { grid: el.component }, dragHandler);
            el.component.option('loadPanel.enabled', false);
            updateTableHeaderWrap();
        }
    };

    return Object.assign({}, staticConfig, functions, dynamicConfig);
}

export default config;
